// VisionScreeningPage.tsx
import React, { useMemo, useState, useEffect, JSX } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../../components/Center Admin/layout/AppLayout";
import "../../center admin css/Nurse3Page.css";
import "../../center admin css/centerAdmin.css";
import OptimetricActivePatientCard from "../../components/Center Admin/Reuse/OptometryActivePatientCard";

// === Forms (same set used in Nurse1) ===
import { AnthropometricMeasurementForm } from "../../components/Center Admin/forms/AnthropometricMeasurementForm";
import { PhysicalExaminationForm } from "../../components/Center Admin/forms/PhysicalExaminationForm";
import { ADMinorSymptomsExaminationForm } from "../../components/Center Admin/forms/ADMinorSymptomsExaminationForm";
import { BUSampleForm } from "../../components/Center Admin/forms/BUSampleForm";
import BnuSampleForm from "../../components/Center Admin/forms/sentToLab";
import LabReportForm from "../../components/Center Admin/forms/LabReportForm";
import { VisionScreeningForm } from "../../components/Center Admin/forms/VisionScreeningForm";
import { OralImagingForm } from "../../components/Center Admin/forms/OralImagingForm";
import { VitalsExaminationForm } from "../../components/Center Admin/forms/VitalsExaminationForm";
import { SalivaSampleForm } from "../../components/Center Admin/forms/SalivaSampleForm";
import { CariogramInputForm } from "../../components/Center Admin/forms/CariogramInputForm";
import AudiologyScreeningForm from "../../components/Center Admin/forms/AudiologyScreeningForm";

// ===== Helpers & types (same pattern as Nurse1/2/3) =====
const normalize = (s?: string) =>
  (s ?? "").toString().trim().toUpperCase().replace(/\s+/g, " ");

type BackendForm = {
  _id?: string;
  name?: string;
  key?: string;
  category?: string;
  isActive?: boolean;
};

type AllowedForm = {
  id: string;
  label: string;
  render: (emrId: string) => JSX.Element;
};

/** 🔁 Robust user loader: checks multiple keys in session & local storage */
function loadUserFromStorage(): any | null {
  const keys = [
    "currentUser",
    "CurrrentResponse",
    "CurrentResponse",
    "backendResponse",
  ];
  const stores: Storage[] = [sessionStorage, localStorage];

  const read = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  for (const s of stores) {
    for (const k of keys) {
      const v = read(s, k);
      if (v && typeof v === "object") return v;
    }
  }
  return null;
}

export const VisionScreeningPage: React.FC = () => {
  const { activePatient } = useOutletContext<any>();

  const emrId = activePatient?.child?.childId || "";

  // useEffect(() => {
  //   setFormStatus({ vision: false });
  // }, [activePatient]);

  useEffect(() => {
    setFormStatus({
      vision: false,
    });

    if (!activePatient) {
      setIsStarted(false);
    }
  }, [activePatient]);

  // dynamic forms state (same behavior as Nurse1)
  const [loadingForms, setLoadingForms] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visibleForms, setVisibleForms] = useState<AllowedForm[]>([]);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [formStatus, setFormStatus] = useState({
    vision: false,
  });

  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    const handler = () => {
      setIsStarted(true);
    };

    window.addEventListener("patientUpdated", handler);

    return () => {
      window.removeEventListener("patientUpdated", handler);
    };
  }, []);

  const updateFormStatus = (key: string, value: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateBeforeComplete = () => {
    if (!formStatus.vision) {
      alert("Please complete Vision Screening form");
      return false;
    }
    return true;
  };

  const FORM_COMPONENTS: Record<string, (emrId: string) => JSX.Element> = {
    ANTHROPOMETRY: (emrId) => <AnthropometricMeasurementForm emrId={emrId} />,
    "ANTHROPOMETRIC MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "ANTHROPOMETRY MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm emrId={emrId} />
    ),
    "PHYSICAL EXAMINATION": (emrId) => (
      <PhysicalExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYSPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "ADMINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "AD MINOR SYMPTOMS": (emrId) => (
      <ADMinorSymptomsExaminationForm emrId={emrId} />
    ),
    "BLOOD AND URIN SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm emrId={emrId} />
    ),
    "BLOOD AND URINE SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm emrId={emrId} />
    ),
    "BLOOD SAMPLE": (emrId) => <BUSampleForm emrId={emrId} />,
    "BLOOD SAMPLE SENT TO LAB": (emrId) => <BnuSampleForm emrId={emrId} />,
    "LAB REPORT": (emrId) => <LabReportForm emrId={emrId} />,

    "VISION SCREENING": (emrId) => (
      <VisionScreeningForm
        emrId={emrId}
        onComplete={() => updateFormStatus("vision", true)}
      />
    ),

    "ORAL IMAGING": (emrId) => <OralImagingForm emrId={emrId} />,
    "VITALS EXAMINATION": (emrId) => <VitalsExaminationForm emrId={emrId} />,
    "CARIOGRAM SALAIVA SAMPLE": (emrId) => <SalivaSampleForm emrId={emrId} />,
    "CARIOGRAM INPUT": (emrId) => <CariogramInputForm emrId={emrId} />,
    "AUDIOLOGY SCREENING": (emrId) => <AudiologyScreeningForm emrId={emrId} />,
  };

  useEffect(() => {
    setLoadingForms(true);
    setLoadError(null);

    try {
      const user = loadUserFromStorage();
      if (!user) {
        throw new Error("User data not found in session. Please log in again.");
      }

      // Prefer role-based form list, fallback to user.forms
      const fromRole: BackendForm[] =
        user?.roleAssignments?.[0]?.roleId?.formIds ?? [];
      const fallback: BackendForm[] = user?.forms ?? [];
      const backendForms: BackendForm[] =
        (fromRole?.length ? fromRole : fallback)?.filter(
          (f) => f?.isActive !== false,
        ) ?? [];

      const built: AllowedForm[] = [];
      const seen = new Set<string>();

      backendForms.forEach((bf) => {
        const nameN = normalize(bf?.name);
        const keyN = normalize(bf?.key);
        const renderer =
          FORM_COMPONENTS[nameN] ?? FORM_COMPONENTS[keyN] ?? null;
        if (!renderer) return;

        const label = bf?.name || bf?.key || "Form";
        const unique = bf?._id || `${label}-${keyN || nameN}`;
        if (!seen.has(unique)) {
          seen.add(unique);
          built.push({ id: unique, label, render: renderer });
        }
      });

      setVisibleForms(built);
      setActiveIdx((prev) => (built[prev] ? prev : 0));

      if (built.length === 0) {
        setTimeout(() => {
          window.alert(
            "No form is assigned to your role. Please contact the Super Admin.",
          );
        }, 0);
      }
    } catch (err: any) {
      console.error("VisionScreeningPage: failed to load user forms", err);
      setLoadError(err.message || "Could not load permitted forms.");
      setVisibleForms([]);
      setActiveIdx(0);
    } finally {
      setLoadingForms(false);
    }
  }, []);

  return (
    <section className="bnu-main">
      {/* Patient header (same as Nurse1) */}

      <OptimetricActivePatientCard
        onPatientSelect={(patient) => console.log("Selected:", patient)}
        showActions={true}
        validateBeforeComplete={validateBeforeComplete}
        onStationStarted={() => setIsStarted(true)}
      />

      {/* Dynamic sub-tabs + renderer */}
      <div className="nurse-subtabs">
        {loadingForms && <div className="nurse-subtab disabled">Loading…</div>}

        {!loadingForms && loadError && (
          <div className="nurse-subtab disabled">{loadError}</div>
        )}

        {!loadingForms && !loadError && visibleForms.length > 0 && (
          <>
            {visibleForms.map((f, i) => (
              <button
                key={f.id}
                className={`nurse-subtab${activeIdx === i ? " active" : ""}`}
                type="button"
                onClick={() => setActiveIdx(i)}
                title={f.label}
              >
                {f.label}
              </button>
            ))}
          </>
        )}

        {!loadingForms && !loadError && visibleForms.length === 0 && (
          <div className="nurse-subtab disabled">No forms assigned</div>
        )}
      </div>

      {activePatient &&
        isStarted &&
        !loadingForms &&
        !loadError &&
        visibleForms[activeIdx] && <>{visibleForms[activeIdx].render(emrId)}</>}
    </section>
  );
};

export default VisionScreeningPage;
